/**
 * Scale & send handlers for stats tab.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback } from 'react';

import { useToast } from '@/providers/ToastProvider';
import type { Gateway, LeadsList, ShortLinkTld } from '@/hooks';
import type { AiTextMetadataPayload } from '@/hooks/smswiser-campaign-workspace/types';
import {
  useCountryCreative,
  useCreatePreparedDropFlex,
  useDeleteCountrySender,
  useDeleteCountryText,
  useDeleteCountryTextsBulk,
  useSendPreparedDropFlex,
  useUpdateCountryCreative,
} from '@/hooks';
import {
  filterSenderNamesMax11,
  uniqueKeepOrderStrings,
} from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';
import { formatDateDMY } from '@/features/smswiser-campaigns/utils/dateFormat';
import { getApiErrorMessage } from '@/lib/utils';
import type { DropsWithStatsRow } from '@/features/smswiser-campaigns/types/workspace.types';
import { executeScaleConfirm } from './handlers/scaleConfirmHandler';
import { executeCopyAsDraft } from './handlers/copyAsDraftHandler';

export function useScaleDropHandlers(
  countryKey: string,
  /** Fallback when campaigns empty (legacy single-campaign view). */
  primaryWiserId: number,
  leadsLists: LeadsList[],
  routes: Gateway[],
  tlds: ShortLinkTld[],
  /** Campaign list (id+name) to resolve each drop's campaign → wiserId. Required for per-drop campaign preservation. */
  campaigns: Array<{ id: number; name: string }>,
  onScaleAndSendComplete?: () => void,
  /** Clear selection after scale/copy. Uses (prev) => [] when clearing. */
  setSelectedRows?: (updater: (prev: unknown[]) => unknown[]) => void,
  setShowScaleModal?: (show: boolean) => void,
  setShowCopyAsDraftModal?: (show: boolean) => void,
) {
  const { success: toastSuccess, error: toastError, loading: toastLoading, dismiss: toastDismiss } = useToast();
  const creative = useCountryCreative(countryKey);
  const updateCreative = useUpdateCountryCreative(countryKey);
  const deleteCampaignSender = useDeleteCountrySender(countryKey);
  const deleteCampaignText = useDeleteCountryText(countryKey);
  const deleteCampaignTextsBulk = useDeleteCountryTextsBulk(countryKey);
  const createPreparedFlex = useCreatePreparedDropFlex();
  const sendPreparedFlex = useSendPreparedDropFlex();

  const handleAddSender = useCallback(
    async (name: string) => {
      if (name.length > 11) return;
      const next = uniqueKeepOrderStrings([...(creative.data?.senderNames ?? []), name]).slice(0, 50);
      await updateCreative.mutateAsync({ senderNames: filterSenderNamesMax11(next) });
    },
    [creative.data?.senderNames, updateCreative],
  );

  const handleAddText = useCallback(
    async (texts: string[], aiTextMetadata?: AiTextMetadataPayload[]) => {
      const merged = uniqueKeepOrderStrings([...(creative.data?.smsTexts ?? []), ...texts]).slice(0, 50);
      await updateCreative.mutateAsync({ smsTexts: merged, aiTextMetadata });
    },
    [creative.data?.smsTexts, updateCreative],
  );

  const handleDeleteSender = useCallback(
    async (name: string) => {
      await deleteCampaignSender.mutateAsync(name);
    },
    [deleteCampaignSender],
  );

  const handleDeleteText = useCallback(
    async (text: string) => {
      await deleteCampaignText.mutateAsync(text);
    },
    [deleteCampaignText],
  );

  const handleDeleteTextsBulk = useCallback(
    async (texts: string[]) => {
      await deleteCampaignTextsBulk.mutateAsync(texts);
    },
    [deleteCampaignTextsBulk],
  );

  const handleScaleConfirm = useCallback(
    async (payload: Parameters<typeof executeScaleConfirm>[0], multiRows?: DropsWithStatsRow[]) => {
      const loadingId = toastLoading('Scaling and sending...', 'Please wait');
      try {
        await executeScaleConfirm(payload, multiRows, {
          countryKey,
          campaigns,
          primaryWiserId,
          leadsLists,
          routes,
          tlds,
          creativeSmsTexts: creative.data?.smsTexts,
          updateCreative,
          createPreparedFlex,
          sendPreparedFlex,
          toastError,
        });
        setSelectedRows?.(() => []);
        setShowScaleModal?.(false);
        toastDismiss(loadingId);
        const rows = multiRows && multiRows.length > 0 ? multiRows : [];
        toastSuccess('Scaled successfully', `Scaled ${rows.length} drop${rows.length !== 1 ? 's' : ''} successfully.`);
        onScaleAndSendComplete?.();
      } catch (e) {
        toastDismiss(loadingId);
        toastError('Scale & send failed', getApiErrorMessage(e, 'Could not create or send drop.'));
        throw e;
      }
    },
    [
      countryKey,
      creative.data?.smsTexts,
      updateCreative,
      createPreparedFlex,
      sendPreparedFlex,
      campaigns,
      primaryWiserId,
      leadsLists,
      routes,
      tlds,
      onScaleAndSendComplete,
      toastSuccess,
      toastError,
      toastLoading,
      toastDismiss,
      setSelectedRows,
      setShowScaleModal,
    ],
  );

  const handleCopyAsDraft = useCallback(
    async (
      rows: DropsWithStatsRow[],
      targetWorkDate: string,
      gatewayOverrides?: Record<string, number>,
      staticTagId?: string | null,
    ) => {
      try {
        const created = await executeCopyAsDraft(
          rows,
          targetWorkDate,
          {
            countryKey,
            campaigns,
            primaryWiserId,
            leadsLists,
            routes,
            tlds,
            creativeSmsTexts: creative.data?.smsTexts,
            updateCreative,
            createPreparedFlex,
            toastError,
          },
          gatewayOverrides,
          staticTagId,
        );
        setSelectedRows?.(() => []);
        setShowCopyAsDraftModal?.(false);
        if (created > 0) {
          toastSuccess('Copied as draft', `${created} drop(s) created as draft for ${formatDateDMY(targetWorkDate)}. Check Prepared drops tab.`);
        }
      } catch (e) {
        toastError('Copy as draft failed', getApiErrorMessage(e, 'Could not create drafts.'));
        throw e;
      }
    },
    [
      countryKey,
      creative.data?.smsTexts,
      updateCreative,
      createPreparedFlex,
      campaigns,
      primaryWiserId,
      leadsLists,
      routes,
      tlds,
      toastSuccess,
      toastError,
      setSelectedRows,
      setShowCopyAsDraftModal,
    ],
  );

  return {
    handleAddSender,
    handleAddText,
    handleDeleteSender,
    handleDeleteText,
    handleDeleteTextsBulk,
    handleScaleConfirm,
    handleCopyAsDraft,
    creative,
    createPrepared: createPreparedFlex,
    sendPrepared: sendPreparedFlex,
    updateCreative,
    deleteCampaignSender,
    deleteCampaignText,
    deleteCampaignTextsBulk,
  };
}
